export const PRIVACY_POLICY = `
# Privacy Policy

**Last updated: February 18, 2026**

## 1. Introduction

Welcome to Care. We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use our application.

## 2. Information We Collect

### Information You Provide
- **Account Information:** Name, email address, date of birth, gender, and optional profile photo.
- **Health Data:** Moods, symptoms, medications, food logs, and insights you choose to record.
- **Community Content:** Insights, comments, and likes you share with other users.

### Information Collected Automatically
- **Device Information:** Device type, operating system, and browser type.
- **Usage Data:** Pages visited, features used, and interaction timestamps.
- **Push Notification Tokens:** If you opt in to push notifications.

## 3. How We Use Your Information

We use the information we collect to:
- Provide, maintain, and improve the Care application.
- Authenticate your identity and secure your account.
- Send you medication reminders and notifications you have opted into.
- Display community content to other registered users.
- Respond to your requests and provide support.

## 4. Data Sharing

We do **not** sell your personal data. We may share information only in these circumstances:
- **With your consent** when you post insights to the community feed.
- **For legal compliance** if required by law, regulation, or legal process.
- **Service providers** who assist in operating our application (e.g., hosting, email delivery), bound by confidentiality obligations.

## 5. Data Security

We implement industry-standard security measures including:
- Encrypted data transmission (HTTPS/TLS).
- Secure password hashing (bcrypt).
- JWT-based authentication with token expiration.
- Email verification for new accounts.

## 6. Data Retention

We retain your data for as long as your account is active. If you delete your account, your personal data will be permanently removed from our systems. Community content (insights, comments) associated with deleted accounts may be anonymized rather than deleted.

## 7. Your Rights

You have the right to:
- **Access** your personal data through your account settings.
- **Update** your information at any time.
- **Delete** your account and associated data.
- **Opt out** of push notifications.

## 8. Children's Privacy

Care is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.

## 9. Changes to This Policy

We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy within the application.

## 10. Contact Us

If you have any questions about this Privacy Policy, please email us at care.netlify.app@gmail.com.
`;

export const TERMS_OF_SERVICE = `
# Terms of Service

**Last updated: February 18, 2026**

## 1. Acceptance of Terms

By accessing or using Care, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the application.

## 2. Description of Service

Care is a personal health tracking and community application that allows users to:
- Track moods, symptoms, medications, and food intake.
- Record and manage daily affirmations.
- Share insights and interact with other users in the community.

## 3. Account Registration

- You must provide accurate and complete information when creating an account.
- You must verify your email address to access all features.
- You are responsible for maintaining the confidentiality of your account credentials.
- You must be at least 13 years old to create an account.

## 4. User Conduct

You agree not to:
- Use the service for any unlawful purpose.
- Post content that is abusive, harassing, threatening, or hateful.
- Impersonate another person or misrepresent your identity.
- Attempt to gain unauthorized access to other users' accounts or data.
- Use automated systems to access the service without permission.
- Upload malicious code or interfere with the service's operation.

## 5. User Content

- You retain ownership of the content you create (moods, insights, comments, etc.).
- By posting content to the community, you grant Care a non-exclusive license to display that content to other users.
- You are solely responsible for the content you post.
- We reserve the right to remove content that violates these terms.

## 6. Health Disclaimer

Care is **not** a medical application and does not provide medical advice. The tracking features are for personal informational purposes only. Always consult a qualified healthcare professional for medical decisions. We are not responsible for any health-related decisions made based on data recorded in the app.

## 7. Intellectual Property

- The Care application, its design, and its codebase are the property of the developer.
- The Care logo and branding are protected intellectual property.
- Care is an open-source project; code contributions are subject to the repository's license.

## 8. Service Availability

- We strive to keep Care available at all times but do not guarantee uninterrupted service.
- We may modify, suspend, or discontinue features at any time.
- We are not liable for any loss of data due to service interruptions.

## 9. Account Termination

- You may delete your account at any time from the Settings page.
- We reserve the right to suspend or terminate accounts that violate these terms.
- Upon termination, your personal data will be deleted in accordance with our Privacy Policy.

## 10. Limitation of Liability

Care is provided "as is" without warranties of any kind. To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, or consequential damages arising from your use of the service.

## 11. Changes to Terms

We may update these Terms of Service at any time. Continued use of the service after changes constitutes acceptance of the new terms.

## 12. Contact

If you have questions about these Terms of Service, please email us at care.netlify.app@gmail.com.
`;
